<script lang="ts" setup>
import FileInput from '@/components/input/FileInput.vue';
import Flex from '@/components/atom/Flex.vue';
import { i18n } from '@/i18n';
import { computed, onMounted, ref } from 'vue';
import InputExcelForm from '../inputExcelForm/InputExcelForm.vue';
import InputOrderExcelForm from '../inputExcelForm/InputOrderExcelForm.vue';
import { useImportExcelStore } from '../../ImportExcelStore';
import ResultRegisteredAnnounceModal from '@/registerInputModule/pages/components/ResultRegisteredAnnounceModal.vue';
import { RegisterSettingStore } from '../../RegisterSettingStore';
import { storeToRefs } from 'pinia';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { useRouter } from 'vue-router';
import { InputFileType, inputFileTypes } from '@/coreDomainsModule/entities/inputExcelInfo';
import { assertNever } from '@/coreDomainsModule/assertNever';
import type { RegisterOrderExcelInfos } from '@/coreDomainsModule/entities/InputOrderExcelInfo';

// STOPSHIP: ざっくり移行しているだけなので、リファクタ必須
/**
 * ホーム画面上で年月情報を編集できる入力ファイルタイプ
 */
type StandardInputFileType = Exclude<InputFileType, 'Order'>;

const store = useImportExcelStore();
const importExcelState = store.state;
const router = useRouter();

const props = defineProps<{
  registerSettingStore: RegisterSettingStore;
}>();
const { state } = storeToRefs(props.registerSettingStore);

const t = i18n.t;
const fileInputRef1 = ref();
const fileInputRef2 = ref();
const fileInputRef3 = ref();
const fileInputRef4 = ref();
const fileInputRef5 = ref();
const fileInputRef6 = ref();
const fileInputRef7 = ref();
const fileInputRef8 = ref();

const isInputProcessing = ref(false);

onMounted(async () => {
  await props.registerSettingStore.fetchInputHomeData();
  store.setupFileInputRef(
    fileInputRef1,
    fileInputRef2,
    fileInputRef3,
    fileInputRef4,
    fileInputRef5,
    fileInputRef6,
    fileInputRef7,
    fileInputRef8
  );
});

const importData = computed(() => {
  return store.importUpdateData(importExcelState.importFlags);
});

// 少なくとも 1 つのファイルが選択されていることをチェックする
const checkSelectedFileExport = computed(() => {
  return Object.keys(store.outputFileSelectionButton.value).some((key) => store.outputFileSelectionButton.value[key].active);
});

/**
 * 登録スタートボタンの有効/無効を判定する
 */
const availableStartButton = computed(() => {
  return (
    (!(importData.value && (checkSelectedFileExport.value || importExcelState.translateMode === 'updateExcel')) ||
      state.value.homeInput.isEdited.Inventory ||
      state.value.homeInput.isEdited.SalesResults ||
      state.value.homeInput.isEdited.Arrival) &&
    !isInputProcessing.value
  );
});

/**
 * setter
 * @param inputFileType - ファイルタイプ
 * @param updateValue - 更新する値
 */
const updateInlineHeaderName = (inputFileType: StandardInputFileType, updateValue: string) => {
  const inputExcelInfo = state.value.homeInput.inputExcelInfo?.[inputFileType];
  if (!inputExcelInfo) {
    return;
  }
  inputExcelInfo.inlinedYearMonthHeaderName = {
    headerName: updateValue,
    validateError: 'none',
  };
  state.value.homeInput.isEdited[inputFileType] = true;
};

/**
 * setter
 * @param inputFileType - ファイルタイプ
 * @param updateValue - 更新する値
 */
const updateInHeaderStartColumnNumber = (inputFileType: StandardInputFileType, updateValue: string) => {
  const inputExcelInfo = state.value.homeInput.inputExcelInfo?.[inputFileType];
  const inHeaderYearMonthInfo = inputExcelInfo?.inHeaderYearMonthInfo;
  if (!inputExcelInfo || !inHeaderYearMonthInfo) {
    return;
  }
  inputExcelInfo.inHeaderYearMonthInfo = {
    ...inHeaderYearMonthInfo,
    startColumnNumber: {
      ...inHeaderYearMonthInfo.startColumnNumber,
      raw: updateValue,
    },
  };
  state.value.homeInput.isEdited[inputFileType] = true;
};

/**
 * setter
 * @param inputFileType - ファイルタイプ
 * @param updateValue - 更新する値
 */
const updateInHeaderEndColumnNumber = (inputFileType: StandardInputFileType, updateValue: string) => {
  const inputExcelInfo = state.value.homeInput.inputExcelInfo?.[inputFileType];
  const inHeaderYearMonthInfo = inputExcelInfo?.inHeaderYearMonthInfo;
  if (!inputExcelInfo || !inHeaderYearMonthInfo) {
    return;
  }
  inputExcelInfo.inHeaderYearMonthInfo = {
    ...inHeaderYearMonthInfo,
    endColumnNumber: {
      ...inHeaderYearMonthInfo.endColumnNumber,
      raw: updateValue,
    },
  };
  state.value.homeInput.isEdited[inputFileType] = true;
};

/**
 * setter
 * @param inputFileType - ファイルタイプ
 * @param updateValue - 更新する値
 */
const updateInHeaderStart = (inputFileType: StandardInputFileType, updateValue: string) => {
  const inputExcelInfo = state.value.homeInput.inputExcelInfo?.[inputFileType];
  const inHeaderYearMonthInfo = inputExcelInfo?.inHeaderYearMonthInfo;
  if (!inputExcelInfo || !inHeaderYearMonthInfo) {
    return;
  }
  inputExcelInfo.inHeaderYearMonthInfo = {
    ...inHeaderYearMonthInfo,
    start: {
      ...inHeaderYearMonthInfo.start,
      raw: updateValue,
    },
  };
  state.value.homeInput.isEdited[inputFileType] = true;
};

/**
 * setter
 * @param inputFileType - ファイルタイプ
 * @param updateValue - 更新する値
 */
const updateInHeaderEnd = (inputFileType: StandardInputFileType, updateValue: string) => {
  const inputExcelInfo = state.value.homeInput.inputExcelInfo?.[inputFileType];
  const inHeaderYearMonthInfo = inputExcelInfo?.inHeaderYearMonthInfo;
  if (!inputExcelInfo || !inHeaderYearMonthInfo) {
    return;
  }
  inputExcelInfo.inHeaderYearMonthInfo = {
    ...inHeaderYearMonthInfo,
    end: {
      ...inHeaderYearMonthInfo.end,
      raw: updateValue,
    },
  };
  state.value.homeInput.isEdited[inputFileType] = true;
};

/**
 * ホーム画面上の発注取込設定を更新する
 * @param orderExcelInfos - 更新後の発注取込設定一覧
 */
const updateHomeOrderExcelInfos = (orderExcelInfos: RegisterOrderExcelInfos) => {
  if (!state.value.homeInput.inputExcelInfo) return;
  state.value.homeInput.inputExcelInfo = {
    ...state.value.homeInput.inputExcelInfo,
    Order: orderExcelInfos,
  };
  state.value.homeInput.isEdited.Order = true;
};

/**
 * ホーム画面上の発注取込設定を削除して保存する
 * @param orderExcelInfos - 削除後の発注取込設定一覧
 */
const removeHomeOrderExcelInfo = async (orderExcelInfos: RegisterOrderExcelInfos) => {
  updateHomeOrderExcelInfos(orderExcelInfos);
  await props.registerSettingStore.onClickHomeInputUpdateData('Order');
};

/**
 * ホーム画面上の発注取込設定名を保存する
 */
const confirmHomeOrderExcelInfoName = async () => {
  await props.registerSettingStore.onClickHomeInputUpdateData('Order');
};

/**
 * 登録スタートボタン押下時のハンドラ
 */
const handleConfirm = async () => {
  isInputProcessing.value = true;
  const el = document.activeElement;
  if (el instanceof HTMLElement) el.blur();
  const response = await store.openTranslateExcelModal();
  if (response && response.pushToOrderPage) {
    router.push('/order');
  }
  isInputProcessing.value = false;
};

const selectedInputSetting = ref<InputFileType>('Inventory');

/**
 * タイプ
 * @param type - 変更するタイプ
 */
const selectInputSetting = (type: InputFileType) => {
  selectedInputSetting.value = type;
};

/**
 * ファイルタイプに応じたタイトルのラベルを返す
 * @param type - タイプ
 */
const getTitleByInputFileType = (type: InputFileType) => {
  switch (type) {
    case 'Inventory':
      return '在庫ファイル';
    case 'Arrival':
      return '入荷ファイル';
    case 'SalesResults':
      return '販売実績ファイル';
    case 'Order':
      return '発注取込ファイル';
    default:
      assertNever(type);
      return '';
  }
};
</script>

<template>
  <div class="home-input">
    <div class="box_container">
      <div class="box_input">
        <h4 class="box_input__title">
          {{ t('入力（ドラッグ&ドロップまたはファイル選択）') }}<br />
          {{ t('※ファイル名の冒頭（在庫表_など）と拡張子は入力規則に則ってください') }}
        </h4>
        <div class="box_input__body">
          <section class="section">
            <div class="box">
              <div class="mb-20px dropzone" v-bind="store.getRootProps()">
                <input :data-testid="`dropzone_input_area`" v-bind="store.getInputProps()" />
                <div v-if="importExcelState.files.length > 0" :data-testid="`input_file_content`" class="dz-file-preview dz-preview">
                  <div v-for="(file, index) in importExcelState.files" :key="index" class="dz-file-content">
                    <div v-if="store.isZipFile(file)" class="dz-file-preview-zip dz-details">
                      <div class="file-name-and-size">
                        <span>{{ store.fetchNameForEvent(file) }}</span>
                        <span>{{ store.getFileSizeStr(file) }}</span>
                      </div>
                      <span
                        :data-testid="`dropzone_item_delete_button_${index}`"
                        class="dz-remove"
                        @click.stop="store.handleClickDeleteFile(index)"
                      >
                        {{ t('削除') }}
                      </span>
                    </div>
                    <div v-else class="dz-details">
                      <div class="file-name-and-size">
                        <span>{{ store.fetchNameForEvent(file) }}</span>
                        <span>{{ store.getFileSizeStr(file) }}</span>
                      </div>
                      <span class="dz-remove" @click.stop="store.handleClickDeleteFile(index)"> Delete </span>
                    </div>
                  </div>
                </div>
                <div v-else class="dz-default">
                  <div class="dz-message" :class="store.isDragActive">
                    <i />
                    <!-- STOPSHIP: 入力されたファイルのUIが微妙なので修正する -->
                    <p>{{ t('ここにファイルをドロップ') }}</p>
                  </div>
                </div>
              </div>
              <div class="dropzone_output">
                <Flex :flex-direction="'column'" :align-items="'baseline'">
                  <div class="form-label">{{ t('在庫ファイル') + `（${t('在庫_XXX.xlsx')}）` }}</div>
                  <Flex>
                    <FileInput
                      ref="fileInputRef2"
                      :data-testid="`input_inventory_excel_button`"
                      :prop-file-name="store.fetchFileName('在庫')"
                      class="file-input"
                      :label="t('Excel取込')"
                      @change="($event) => store.fileRead($event, '在庫', false, 'xlsx')"
                    />
                  </Flex>
                </Flex>
                <Flex :flex-direction="'column'" :align-items="'baseline'">
                  <div class="form-label">{{ t('内示ファイル') + `（${t('内示_XXX.zip')}）` }}</div>
                  <Flex>
                    <FileInput
                      ref="fileInputRef1"
                      :data-testid="`input_announce_excel_button`"
                      :prop-file-name="store.fetchFileName('内示')"
                      class="file-input"
                      :label="t('Zip取込')"
                      @change="($event) => store.createIBusinessFromZip($event, false)"
                    />
                  </Flex>
                </Flex>
                <Flex :flex-direction="'column'" :align-items="'baseline'">
                  <div class="form-label">{{ t('日別内示ファイル') + `（${t('日別内示_XXX.zip')}）` }}</div>
                  <Flex>
                    <FileInput
                      ref="fileInputRef7"
                      :data-testid="`input_daily_announce_excel_button`"
                      :prop-file-name="store.fetchFileName('日別内示')"
                      class="file-input"
                      :label="t('Zip取込')"
                      @change="($event) => store.createIBusinessFromDailyZip($event, false)"
                    />
                  </Flex>
                </Flex>
                <Flex :flex-direction="'column'" :align-items="'baseline'">
                  <div class="form-label">{{ t('販売実績ファイル') + `（${t('販売実績_XXX.xlsx')}）` }}</div>
                  <Flex>
                    <FileInput
                      ref="fileInputRef3"
                      :data-testid="`input_sales_results_excel_button`"
                      :prop-file-name="store.fetchFileName('販売実績')"
                      class="file-input"
                      :label="t('Excel取込')"
                      @change="($event) => store.fileRead($event, '販売実績', false, 'xlsx')"
                    />
                  </Flex>
                </Flex>
                <Flex :flex-direction="'column'" :align-items="'baseline'">
                  <div class="form-label">{{ t('入荷ファイル') + `（${t('入荷_XXX.xlsx')}）` }}</div>
                  <Flex>
                    <FileInput
                      ref="fileInputRef4"
                      :data-testid="`input_arrival_excel_button`"
                      :prop-file-name="store.fetchFileName('入荷')"
                      class="file-input"
                      :label="t('Excel取込')"
                      @change="($event) => store.fileRead($event, '入荷', false, 'xlsx')"
                    />
                  </Flex>
                </Flex>
                <Flex :flex-direction="'column'" :align-items="'baseline'">
                  <div class="form-label">{{ t('発注取込ファイル') + `（${t('発注取込_XXX.xlsx')}）` }}</div>
                  <Flex>
                    <FileInput
                      ref="fileInputRef8"
                      :data-testid="`input_order_excel_button`"
                      :prop-file-name="store.fetchFileName('発注取込')"
                      class="file-input"
                      :label="t('Excel取込')"
                      @change="($event) => store.fileRead($event, '発注取込', false, 'xlsx')"
                    />
                  </Flex>
                </Flex>
              </div>
              <div class="py-30px box_input__button-container">
                <TextButton
                  :button-style="'btn-secondary'"
                  :size="'btn-md'"
                  :on-click="registerSettingStore.onClickHomeInputBack"
                  :disabled="false"
                >
                  {{ t('戻る') }}
                </TextButton>

                <div class="box_input__button-container--spacer" />

                <TextButton :button-style="'btn-secondary-error'" :size="'btn-md'" :on-click="store.removeAllFile" :disabled="false">
                  {{ t('リセット') }}
                </TextButton>

                <!-- NOTE: checkSelectedFileExportはstate.translateMode === 'updateExcel'以外の場合にしか評価したくないためこうなっている -->
                <TextButton :button-style="'btn-primary'" :size="'btn-md'" :on-click="handleConfirm" :disabled="availableStartButton">
                  {{ t('登録スタート') }}
                </TextButton>
              </div>
            </div>
          </section>
        </div>
      </div>
      <div class="box-input-file-settings">
        <div class="box-input-file-settings__tabs">
          <div
            v-for="type in inputFileTypes"
            :key="`input-setting-${type}`"
            class="box-input-file-settings__tab"
            :class="{
              'box-input-file-settings__tab--active': type === selectedInputSetting,
            }"
          >
            <button type="button" class="box-input-file-settings__tab__label" @click="selectInputSetting(type)">
              {{ t(getTitleByInputFileType(type)) }}
            </button>
          </div>
        </div>
        <div v-if="selectedInputSetting === 'Inventory'" class="box-input-file-settings__box">
          <div v-if="state.homeInput.inputExcelInfo && state.homeInput.inputExcelInfo.Inventory" class="box-input-file-settings--inventory">
            <InputExcelForm
              :from="'HomeInput'"
              :selected-field-id="''"
              :suggestions="[]"
              :use-format-type="state.homeInput.inputExcelInfo?.Inventory.selectYearMonthPattern"
              :alterable-header-infos="state.homeInput.inputExcelInfo.Inventory.alternateHeaderInfos"
              :inlined-year-month-info="
                state.homeInput.inputExcelInfo.Inventory.inlinedYearMonthHeaderName || {
                  headerName: '',
                  validateError: 'none',
                }
              "
              :in-header-year-month-info="
                state.homeInput.inputExcelInfo.Inventory.inHeaderYearMonthInfo || {
                  startColumnNumber: {
                    raw: '',
                    validateError: 'none',
                  },
                  endColumnNumber: {
                    raw: '',
                    validateError: 'none',
                  },
                  start: {
                    raw: '',
                    validateError: 'none',
                  },
                  end: {
                    raw: '',
                    validateError: 'none',
                  },
                }
              "
              @update-inline-header-name="updateInlineHeaderName('Inventory', $event)"
              @update-in-header-start-column-number="updateInHeaderStartColumnNumber('Inventory', $event)"
              @update-in-header-end-column-number="updateInHeaderEndColumnNumber('Inventory', $event)"
              @update-in-header-start="updateInHeaderStart('Inventory', $event)"
              @update-in-header-end="updateInHeaderEnd('Inventory', $event)"
            />

            <div class="box-input-file-settings__separator" />

            <div class="box-input-file-settings__button-container">
              <TextButton
                :button-style="'btn-secondary'"
                :size="'btn-md'"
                :on-click="
                  () => {
                    registerSettingStore.onClickHomeInputSettingData('在庫');
                  }
                "
                :disabled="false"
              >
                {{ t('取引設定：取引設定ボタン') }}
              </TextButton>
              <TextButton
                :button-style="'btn-primary'"
                :size="'btn-md'"
                :on-click="
                  () => {
                    registerSettingStore.onClickHomeInputUpdateData('Inventory');
                  }
                "
                :disabled="!state.homeInput.isEdited.Inventory"
              >
                {{ t('取引設定：年月情報変更ボタン') }}
              </TextButton>
            </div>
          </div>
          <div v-else class="box-input-file-settings__empty">
            <TextButton
              :button-style="'btn-primary'"
              :size="'btn-md'"
              :on-click="
                () => {
                  registerSettingStore.onClickHomeInputSettingData('在庫');
                }
              "
              :disabled="false"
              class="bulk-action-button-container__export-button"
            >
              {{ t('在庫ファイルの取り込み設定') }}
            </TextButton>
          </div>
        </div>

        <div v-else-if="selectedInputSetting === 'SalesResults'" class="box-input-file-settings__box">
          <div
            v-if="state.homeInput.inputExcelInfo && state.homeInput.inputExcelInfo.SalesResults"
            class="box-input-file-settings--sales-result"
          >
            <InputExcelForm
              :from="'HomeInput'"
              :selected-field-id="''"
              :suggestions="[]"
              :use-format-type="state.homeInput.inputExcelInfo?.SalesResults.selectYearMonthPattern"
              :alterable-header-infos="state.homeInput.inputExcelInfo.SalesResults.alternateHeaderInfos"
              :inlined-year-month-info="
                state.homeInput.inputExcelInfo.SalesResults.inlinedYearMonthHeaderName || {
                  headerName: '',
                  validateError: 'none',
                }
              "
              :in-header-year-month-info="
                state.homeInput.inputExcelInfo.SalesResults.inHeaderYearMonthInfo || {
                  startColumnNumber: {
                    raw: '',
                    validateError: 'none',
                  },
                  endColumnNumber: {
                    raw: '',
                    validateError: 'none',
                  },
                  start: {
                    raw: '',
                    validateError: 'none',
                  },
                  end: {
                    raw: '',
                    validateError: 'none',
                  },
                }
              "
              @update-inline-header-name="updateInlineHeaderName('SalesResults', $event)"
              @update-in-header-start-column-number="updateInHeaderStartColumnNumber('SalesResults', $event)"
              @update-in-header-end-column-number="updateInHeaderEndColumnNumber('SalesResults', $event)"
              @update-in-header-start="updateInHeaderStart('SalesResults', $event)"
              @update-in-header-end="updateInHeaderEnd('SalesResults', $event)"
            />

            <div class="box-input-file-settings__separator" />

            <div class="box-input-file-settings__button-container">
              <TextButton
                :button-style="'btn-secondary'"
                :size="'btn-md'"
                :on-click="
                  () => {
                    registerSettingStore.onClickHomeInputSettingData('販売実績');
                  }
                "
                :disabled="false"
              >
                {{ t('取引設定：取引設定ボタン') }}
              </TextButton>
              <TextButton
                :button-style="'btn-primary'"
                :size="'btn-md'"
                :on-click="
                  () => {
                    registerSettingStore.onClickHomeInputUpdateData('SalesResults');
                  }
                "
                :disabled="!state.homeInput.isEdited.SalesResults"
              >
                {{ t('取引設定：年月情報変更ボタン') }}
              </TextButton>
            </div>
          </div>
          <div v-else class="box-input-file-settings__empty">
            <TextButton
              :button-style="'btn-primary'"
              :size="'btn-md'"
              :on-click="
                () => {
                  registerSettingStore.onClickHomeInputSettingData('販売実績');
                }
              "
              :disabled="false"
              class="bulk-action-button-container__export-button"
            >
              {{ t('販売実績ファイルの取り込み設定') }}
            </TextButton>
          </div>
        </div>

        <div v-else-if="selectedInputSetting === 'Arrival'" class="box-input-file-settings__box">
          <div v-if="state.homeInput.inputExcelInfo && state.homeInput.inputExcelInfo.Arrival" class="box-input-file-settings--arrival">
            <InputExcelForm
              :from="'HomeInput'"
              :selected-field-id="''"
              :suggestions="[]"
              :use-format-type="state.homeInput.inputExcelInfo?.Arrival.selectYearMonthPattern"
              :alterable-header-infos="state.homeInput.inputExcelInfo.Arrival.alternateHeaderInfos"
              :inlined-year-month-info="
                state.homeInput.inputExcelInfo.Arrival.inlinedYearMonthHeaderName || {
                  headerName: '',
                  validateError: 'none',
                }
              "
              :in-header-year-month-info="
                state.homeInput.inputExcelInfo.Arrival.inHeaderYearMonthInfo || {
                  startColumnNumber: {
                    raw: '',
                    validateError: 'none',
                  },
                  endColumnNumber: {
                    raw: '',
                    validateError: 'none',
                  },
                  start: {
                    raw: '',
                    validateError: 'none',
                  },
                  end: {
                    raw: '',
                    validateError: 'none',
                  },
                }
              "
              @update-inline-header-name="updateInlineHeaderName('Arrival', $event)"
              @update-in-header-start-column-number="updateInHeaderStartColumnNumber('Arrival', $event)"
              @update-in-header-end-column-number="updateInHeaderEndColumnNumber('Arrival', $event)"
              @update-in-header-start="updateInHeaderStart('Arrival', $event)"
              @update-in-header-end="updateInHeaderEnd('Arrival', $event)"
            />

            <div class="box-input-file-settings__separator" />

            <div class="box-input-file-settings__button-container">
              <TextButton
                :button-style="'btn-secondary'"
                :size="'btn-md'"
                :on-click="
                  () => {
                    registerSettingStore.onClickHomeInputSettingData('入荷');
                  }
                "
                :disabled="false"
              >
                {{ t('取引設定：取引設定ボタン') }}
              </TextButton>
              <TextButton
                :button-style="'btn-primary'"
                :size="'btn-md'"
                :on-click="
                  () => {
                    registerSettingStore.onClickHomeInputUpdateData('Arrival');
                  }
                "
                :disabled="!state.homeInput.isEdited.Arrival"
              >
                {{ t('取引設定：年月情報変更ボタン') }}
              </TextButton>
            </div>
          </div>
          <div v-else class="box-input-file-settings__empty">
            <TextButton
              :button-style="'btn-primary'"
              :size="'btn-md'"
              :on-click="
                () => {
                  registerSettingStore.onClickHomeInputSettingData('入荷');
                }
              "
              :disabled="false"
              class="bulk-action-button-container__export-button"
            >
              {{ t('入荷ファイルの取り込み設定') }}
            </TextButton>
          </div>
        </div>

        <div v-else-if="selectedInputSetting === 'Order'" class="box-input-file-settings__box">
          <div v-if="state.homeInput.inputExcelInfo && state.homeInput.inputExcelInfo.Order" class="box-input-file-settings--order">
            <InputOrderExcelForm
              from="HomeInput"
              selected-field-id=""
              :suggestions="[]"
              :order-excel-infos="state.homeInput.inputExcelInfo.Order"
              @update-order-excel-infos="updateHomeOrderExcelInfos"
              @remove-order-excel-info="removeHomeOrderExcelInfo"
              @confirm-order-excel-info-name="confirmHomeOrderExcelInfoName"
            />

            <div class="box-input-file-settings__separator" />

            <div class="box-input-file-settings__button-container">
              <TextButton
                :button-style="'btn-secondary'"
                :size="'btn-md'"
                :on-click="
                  () => {
                    registerSettingStore.onClickHomeInputSettingData('発注');
                  }
                "
                :disabled="false"
              >
                {{ t('取引設定：取引設定ボタン') }}
              </TextButton>
            </div>
          </div>
          <div v-else class="box-input-file-settings__empty">
            <TextButton
              :button-style="'btn-primary'"
              :size="'btn-md'"
              :on-click="
                () => {
                  registerSettingStore.onClickHomeInputSettingData('発注');
                }
              "
              :disabled="false"
              class="bulk-action-button-container__export-button"
            >
              {{ t('発注ファイルの取り込み設定') }}
            </TextButton>
          </div>
        </div>
      </div>
    </div>
    <ResultRegisteredAnnounceModal :state="importExcelState" @on-next="importExcelState.isOpenResultRegisteredAnnounceModal = false" />
  </div>
</template>

<style lang="scss" scoped>
.mb-20px {
  margin-bottom: 20px !important;
}
.mb-16px {
  margin-bottom: 16px !important;
}
.mr-8px {
  margin-right: 8px !important;
}
.py-30px {
  padding: 30px 0 !important;
}
.fs-16px {
  font-size: 16px !important;
}
img {
  object-fit: contain;
}
.max-w-300px {
  max-width: 300px !important;
}
.search {
  position: fixed;
  top: 0;
  z-index: 10;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.modal-background {
  opacity: 0.4;
}
.mainImg {
  overflow: hidden;
}
.mainImg img {
  display: block;
  transition-duration: 0.3s;
}
.mainImg img:hover {
  transform: scale(0.9);
  transition-duration: 0.3s;
}
.file-input {
  display: flex;
  margin: 0 0 20px;
}

.execution-date-picker {
  width: 200%;
  margin: 0 0 20px;
}
</style>
<style lang="scss">
.home-input {
  display: grid;
  gap: 16px;
  grid-template-rows: repeat(2, auto);
  background-color: $semanticWhite;
}
.box_container {
  display: grid;
  grid-template-columns: fit-content(100%) 1fr;
  gap: 80px;
}

.box_input {
  max-width: 600px;
  width: 100%;
  margin-top: 16px;
  margin-left: 16px;
  &__select-mode {
    display: grid;
    margin-top: 48px;
    grid-template-rows: repeat(4, fit-content(100%));
  }

  &__button-container {
    width: 100%;
    display: grid;
    justify-self: center;
    gap: 8px;
    grid-template-columns: auto 1fr auto auto;
  }
}

.box-input-file-settings {
  display: grid;
  margin: 16px;
  grid-template-rows: max-content max-content;

  &__tabs {
    display: grid;
    grid-template-columns: repeat(4, max-content);
    gap: 4px;
    height: max-content;
    align-items: end;
  }

  &__tab {
    position: relative;
    z-index: 1;
    padding: 8px;
    border: 1px solid $borderDefault;
    border-bottom: none;
    border-radius: 6px 6px 0 0;
    height: max-content;
    color: $textDefault;

    &--active {
      z-index: 2;
      margin-bottom: -1px;
      background-color: $semanticPrimarySubdued;
      border-color: $borderActive;
      font-weight: 700;
    }
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    color: $textDefault;
  }

  &__box {
    padding: 16px;
    border: 3px dotted $borderDefault;
    max-height: max-content;
    margin-top: -1px;
  }

  &--inventory,
  &--sales-result,
  &--arrival,
  &--order {
    display: grid;
    gap: 16px;
    grid-template-columns: 1fr min-content max-content;
  }

  &__separator {
    background-color: $borderDefault;
    width: 1px;
    margin: 0 16px;
    border-bottom: 1px solid $borderDefault;
  }

  &__button-container {
    align-self: center;
    display: grid;
    gap: 8px;
    grid-template-rows: repeat(2, fit-content(100%));
  }

  &__empty {
    margin: 0px 16px 16px 16px;
    justify-self: center;
  }

  // NOTE: InputExcelFormのスタイルをここ用に変更する
  .linking-container__left {
    grid-template-columns: repeat(auto-fit, minmax(100px, 200px));
  }

  .linking-container__separator {
    margin: 0 16px;
  }
}

.select-mode {
  &__title {
    margin: 0 0 20px;
    font-weight: 500;
    font-size: 17px;
  }
  &__selectable-content {
    display: grid;
    grid-template-columns: 30px 1fr;
    padding-bottom: 16px;
    margin-left: -5px;
  }
}

.box_input__title {
  margin: 0 0 20px;
  font-weight: 500;
  font-size: 17px;
}
.dropzone {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  /* padding: 0px;
  border-color: theme('colors.gray.400'); */
  padding: 0;
  cursor: pointer;
  min-height: 150px;
  background: white;
  color: #021527;
  font-size: 14px;
  border-radius: 14px;
  border: 1px dashed #c8c5c5;
  padding: 20px 25px;
  &.dz-clickable {
    justify-content: center;
  }
}
.dropzone .dz-preview {
  display: flex;
  margin: 0;
  width: 100%;
  min-height: auto;
  flex-flow: column;
}
.dropzone .dz-default .dz-message {
  font-size: 14px;
  i {
    background: url('~@/assets/img/icon-download.png') no-repeat 0 0;
    display: block;
    width: 30px;
    height: 30px;
    margin: 0 auto 18px;
  }
}
.dropzone .dz-preview .dz-file-content:last-child {
  .dz-details {
    margin: 0;
    padding-bottom: 0;
    border: none;
  }
}
.dropzone .dz-preview .dz-progress {
  opacity: 0;
  display: none;
  z-index: 1000;
  pointer-events: none;
  position: absolute;
  height: 16px;
  top: 80%;
  left: 50%;
  width: 80px;
  margin-left: -40px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  overflow: hidden;
}

.dropzone .dz-preview .dz-file-preview .dz-image {
  display: none;
}
.dropzone > .dz-preview .dz-details .dz-remove {
  position: absolute;
  opacity: 1;
  color: rgba(#fb5c5c, 0.6);
  margin: 0 0 0 auto;
  flex-shrink: 0;
  display: block;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  font-size: 0;
  text-align: center;
  bottom: initial;
  top: 12px;
  right: 0;
  &::before {
    content: '\f2ed';
    font-family: 'Font Awesome\ 5 Free';
    font-size: 16px;
    font-weight: 400;
    display: inline-block;
  }
  &:hover {
    color: #fb5c5c !important;
  }
}
// .dropzone .dz-preview .dz-file-content .dz-details {
//   padding: 13px 0px 21px 45px;
//   margin: 1px 0px 27px;
.dropzone .dz-preview .dz-details {
  padding: 0 30px 20px 45px;
  margin: 0 0 20px;
  position: relative;
  background: white;
  color: #021527;
  border-bottom: 1px solid #e1e1e1;
  display: flex;
  min-width: auto;
  flex-grow: 1;
  .dz-size {
    font-size: 12px;
    line-height: 17px;
    color: #7c7e80;
    margin: 0;
    order: 2;
  }
  .dz-filename {
    font-size: 14px;
    line-height: 20px;
    color: #021527;
    margin: 0 0 4px;
    order: 1;
    overflow: initial !important;
    position: relative;
    span.dz-inner-name {
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
    }
    &.tooltip:hover {
      .dz-tooltip {
        display: block;
      }
    }
  }
}
.dropzone .dz-file-preview .dz-details {
  &::before {
    content: '';
    width: 30px;
    height: 41px;
    position: absolute;
    left: 0;
    top: 0;
    background: url('~@/assets/img/file-document.png') no-repeat 0 0;
  }
}
// TODO: 時間ある時にここら辺のcss周りなんとかする
.dz-file-preview-zip {
  &::before {
    content: '' !important;
    width: 30px !important;
    height: 41px !important;
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    background: url('~@/assets/img/file-zip.png') no-repeat 0 0 !important;
  }
}
.dz-tooltip {
  position: absolute;
  bottom: 0;
  display: none;
  max-width: 400px;
  padding-bottom: 5px;
  transform: translateY(-20px);
  &::before {
    content: '';
    display: block;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 5px 5px 0 5px;
    border-color: #000000 transparent transparent transparent;
    position: absolute;
    bottom: 0;
    left: 50%;
  }
  .dz-tooltip-content {
    background: black;
    color: white;
    word-wrap: break-word;
    padding: 5px 10px 4px;
    border-radius: 16px;
    font-size: 12px;
    span {
      white-space: break-spaces !important;
    }
  }
}
.dropzone:hover {
  background: white;
  color: #000;
}
// .dropzone_outputクラスのdivタグを追加する
.dropzone_output {
  display: flex;
  flex-wrap: wrap;
  padding: 0 0 10px;
  border-bottom: 1px solid #e1e1e1;
  .form-label {
    color: #7c7e80;
    margin: 0 0 10px;
  }
  // 「Excel取込」ボタンのスタイルを変更する
  .file-input label {
    line-height: 21px;
    background-color: #f3f3f3;
    color: #021527;
    box-shadow: none;
    border: 1px solid #e3e3e3;
    border-radius: 12px;
    padding: 8px 26px;
    font-size: 14px;
    width: 140px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
      background-color: #ebebeb;
      transition: background-color 0.5s;
    }
    &.selected {
      width: 190px;
      background-color: #d9d9d9;
      &::after {
        margin-left: auto;
      }
    }
    // 名前が長い場合は末尾に“…”で記載する
    span {
      display: block;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
  }
}
.dropzone_output > div {
  flex-grow: 1;
  flex-basis: 50%;
  width: 50%;
  padding: 0 16px 0 0;
}
.section {
  // 「登録スタート」ボタンと「リセット」ボタンのスタイルを変更する
  .lp-btn {
    display: inline-block;
    padding: 8px 26px;
    font-size: 14px;
    cursor: pointer;
    user-select: none;
    border: 1px solid transparent;
    border-radius: 12px;
    color: #fff;
    touch-action: manipulation;
    box-shadow: none;
    &.lp-btn-export {
      background-color: #2ea6d6;
      border-color: #2ea6d6;
      &:hover {
        background-color: #1580ab;
        border-color: #1580ab;
        transition: background-color 0.5s;
      }
      // 非アクティブになった場合のボタンのスタイルを変更する
      &:disabled {
        background-color: rgba(#1580ab, 0.5);
        color: white;
        border-color: rgba(#1580ab, 0.3);
        cursor: default;
      }
    }
    &.lp-btn-reset {
      background-color: #f3f3f3;
      color: #021527;
      border-color: #e3e3e3;
      &:hover {
        background-color: #ebebeb;
        transition: background-color 0.5s;
      }
    }
  }
}
//出力ファイル選択ボタンをスタイリングする
.group_file {
  width: 296px;
  gap: 16px;
  display: flex;
  flex-wrap: wrap;
  button {
    font-family: 'Noto Sans JP';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #b5b5b5;
    width: 140px;
    height: 40px;
    background: #f3f3f3;
    border: 1px solid #e3e3e3;
    border-radius: 12px;
    cursor: pointer;
    &:hover {
      background-color: #ebebeb;
    }
    &.active {
      background: #4dbce9;
      color: white;
      border-color: #4dbce9;
      font-weight: bold;
    }
  }
}
// ぐるぐるマークをスタイリングする
.loading {
  background: rgba(black, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
}
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.file-name-and-size {
  display: flex;
  flex-direction: column;
}

.execution-date-modal {
  display: grid;
  width: 100%;
  height: 400px;
  place-items: start center;
}
</style>
